import type { ButtonVariant } from '@/components/common/Button';
import type {
  BackgroundColourTokenType,
  BackgroundColourType,
} from '@/types/colours';

const darkColours = [
  '--surface-inverse-subtle',
  '--surface-inverse-base',
  '--surface-inverse-strong',
  '--surface-primary',
  '--surface-inverse-primary',
  '--surface-inverse-danger',
  'navy',
];

const buttonColours = {
  navy: {
    border: '--border-inverse-strong',
    backgroundColour: '--transparent',
    backgroundOnHover: '--surface-subtle',
    fontColour: '--text-inverse-strong',
    fontColourHover: '--text-action',
  },
  indigo: {
    border: '--border-inverse-strong',
    backgroundColour: '--transparent',
    backgroundOnHover: '--surface-subtle',
    fontColour: '--text-action',
    fontColourHover: '--text-action',
  },
  'deep-space': {
    border: '--border-inverse-strong',
    backgroundColour: '--transparent',
    backgroundOnHover: '--surface-subtle',
    fontColour: '--text-inverse-strong',
    fontColourHover: '--text-action',
  },
  ultraviolet: {
    border: '--border-inverse-strong',
    backgroundColour: '--transparent',
    backgroundOnHover: '--surface-inverse-primary',
    fontColour: '--text-action',
    fontColourHover: '--text-action',
  },
  white: {
    border: '--border-action',
    backgroundColour: '--transparent',
    backgroundOnHover: '--surface-primary-subtle',
    fontColour: '--text-action',
    fontColourHover: '--text-action',
  },
  default: {
    border: '--border-action',
    backgroundColour: '--transparent',
    backgroundOnHover: '--surface-inverse-primary',
    fontColour: '--text-action',
    fontColourHover: '--text-inverse-strong',
  },
};

type ButtonVariantOptions = {
  backgroundColour: string;
  fontColour: string;
  fontColourHover: string;
  backgroundDisabled?: string;
  disabledFontColour: string;
  backgroundOnHover?: string;
  underlineOnHover?: 'underline';
};

interface ButtonColors {
  [key: string]: {
    [key in ButtonVariant]: ButtonVariantOptions;
  };
}

export const buttonColoursV2: ButtonColors = {
  white: {
    solid: {
      backgroundColour: '--surface-primary',
      backgroundOnHover: '--surface-inverse-primary',
      backgroundDisabled: '--surface-primary',
      fontColour: '--text-inverse-strong',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-inverse-strong',
    },
    text: {
      backgroundColour: '--transparent',
      backgroundDisabled: '--transparent',
      underlineOnHover: 'underline',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-action',
    },
    default: {
      backgroundColour: '--surface-primary',
      backgroundOnHover: '--surface-inverse-primary',
      backgroundDisabled: '--surface-primary',
      fontColour: '--text-inverse-strong',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-inverse-strong',
    },
  },
  'deep-space': {
    solid: {
      backgroundColour: '--surface-subtle',
      backgroundOnHover: '--surface-subtle',
      backgroundDisabled: '--surface-primary',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-inverse-strong',
    },
    text: {
      backgroundColour: '--transparent',
      backgroundDisabled: '--transparent',
      underlineOnHover: 'underline',
      fontColour: '--text-inverse-strong',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-inverse-strong',
    },
    default: {
      backgroundColour: '--surface-subtle',
      backgroundOnHover: '--surface-inverse-primary',
      backgroundDisabled: '--surface-primary',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-action',
    },
  },
  'inverse-subtle': {
    solid: {
      backgroundColour: '--surface-subtle',
      backgroundOnHover: '--surface-subtle',
      backgroundDisabled: '--surface-subtle',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-action',
    },
    text: {
      backgroundColour: '--transparent',
      backgroundDisabled: '--transparent',
      underlineOnHover: 'underline',
      fontColour: '--text-inverse-strong',
      fontColourHover: '--text-action',
      disabledFontColour: '--surface-primary',
    },
    default: {
      backgroundColour: '--surface-inverse-subtle-default',
      backgroundOnHover: '--surface-inverse-subtle-hover',
      backgroundDisabled: '--surface-inverse-subtle-disabled',
      fontColour: '--text-inverse-strong',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-inverse-strong',
    },
  },
  'inverse-subtle-dark': {
    solid: {
      backgroundColour: '--surface-subtle',
      backgroundOnHover: '--surface-subtle',
      backgroundDisabled: '--surface-inverse-disabled',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-action',
    },
    text: {
      backgroundColour: '--transparent',
      backgroundDisabled: '--transparent',
      underlineOnHover: 'underline',
      fontColour: '--text-inverse-strong',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-action',
    },
    default: {
      backgroundColour: '--surface-subtle',
      backgroundOnHover: '--surface-primary-subtle',
      backgroundDisabled: '--surface-subtle',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-action',
    },
  },
};

export const actionButtonColours = {
  light: {
    flat: {
      backgroundColour: '--surface-cool-strong',
      backgroundOnHover: '--surface-primary-subtle',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
    },
    text: {
      backgroundColour: '--transparent',
      backgroundDisabled: '--transparent',
      backgroundOnHover: '--transparent',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
    },
  },
  dark: {
    flat: {
      backgroundColour: '--surface-subtle',
      backgroundOnHover: '--surface-primary-subtle',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
    },
    text: {
      backgroundColour: '--transparent',
      backgroundDisabled: '--transparent',
      backgroundOnHover: '--transparent',
      fontColour: '--text-action',
      fontColourHover: '--text-action',
      disabledFontColour: '--text-action',
    },
  },
};

const navbarThemeColour: Partial<
  Record<BackgroundColourTokenType, 'dark' | 'light'>
> = {
  '--background-primary': 'light',
  '--background-warm-base': 'dark',
  '--background-subtle': 'dark',
} as const;

export const complementaryFont = (colour) => {
  return darkColours.includes(colour)
    ? '--text-inverse-strong'
    : '--text-strong';
};

export const complementaryButton = (backgroundColour) => {
  return buttonColours[backgroundColour] || buttonColours.default;
};

export const complementaryNavbar = (backgroundColour: BackgroundColourType) => {
  return (
    navbarThemeColour[backgroundColour?.token] ||
    navbarThemeColour['--background-subtle']
  );
};
